import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, IconButton, MenuItem, Paper, Switch, Tab, Tabs,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PublishIcon from '@mui/icons-material/Publish';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import { lmsService } from '../../services/api';
import LmsRichEditor from '../../components/lms/LmsRichEditor';
import LmsVersionHistory from '../../components/lms/LmsVersionHistory';

const emptyCourse = {
  title: '',
  description: '',
  body: '',
  published: false,
  group: '',
  position: 0,
};

function canManageLms() {
  try {
    const u = JSON.parse(localStorage.getItem('currentUser') || 'null');
    const p = u?.effective_permissions || u?.role?.permissions || {};
    return p['menu.lms.manage'] === true;
  } catch {
    return false;
  }
}

function statusLabel(s) {
  if (s === 'done') return { label: 'Завершено', color: 'success' };
  if (s === 'assigned') return { label: 'В процесі', color: 'info' };
  return { label: 'Не розпочато', color: 'default' };
}

export default function LmsCoursesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const manage = useMemo(() => canManageLms(), []);
  const tab = manage && searchParams.get('tab') === 'groups' ? 'groups' : 'courses';

  const [rows, setRows] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [courseOpen, setCourseOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [courseForm, setCourseForm] = useState(emptyCourse);
  const [savingCourse, setSavingCourse] = useState(false);

  const [groupOpen, setGroupOpen] = useState(false);
  const [groupForm, setGroupForm] = useState({ name: '', parent: '', position: 0 });
  const [savingGroup, setSavingGroup] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (manage) {
        const [cRes, gRes] = await Promise.all([lmsService.courses(), lmsService.groups()]);
        setRows(Array.isArray(cRes.data) ? cRes.data : cRes.data?.results || []);
        setGroups(Array.isArray(gRes.data) ? gRes.data : []);
      } else {
        const res = await lmsService.courses();
        setRows(Array.isArray(res.data) ? res.data : res.data?.results || []);
        setGroups([]);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Помилка завантаження');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [manage]);

  useEffect(() => { load(); }, [load]);

  const setTab = (next) => {
    if (next === 'groups') setSearchParams({ tab: 'groups' });
    else setSearchParams({});
  };

  const openCreateCourse = () => {
    setEditingId(null);
    setCourseForm(emptyCourse);
    setCourseOpen(true);
  };

  const openEditCourse = async (row) => {
    try {
      const res = await lmsService.course(row.id);
      const c = res.data;
      setEditingId(c.id);
      setCourseForm({
        title: c.title || '',
        description: c.description || '',
        body: c.body || '',
        published: Boolean(c.published),
        group: c.group || '',
        position: c.position || 0,
      });
      setCourseOpen(true);
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
  };

  const saveCourse = async () => {
    if (savingCourse) return;
    const title = (courseForm.title || '').trim();
    if (!title) { alert('Вкажіть назву курсу'); return; }
    setSavingCourse(true);
    try {
      const payload = {
        title,
        description: courseForm.description || '',
        body: courseForm.body || '',
        published: Boolean(courseForm.published),
        visible_role_ids: [],
        group: courseForm.group || null,
        position: Number(courseForm.position) || 0,
      };
      if (editingId) await lmsService.courseUpdate(editingId, payload);
      else await lmsService.courseCreate(payload);
      setCourseOpen(false);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message || 'Помилка збереження');
    } finally {
      setSavingCourse(false);
    }
  };

  const togglePublish = async (row) => {
    try {
      if (row.published) await lmsService.courseUnpublish(row.id);
      else await lmsService.coursePublish(row.id);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
  };

  const deleteCourse = async (row) => {
    if (!window.confirm(`Видалити курс «${row.title}»?`)) return;
    try {
      await lmsService.courseDelete(row.id);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
  };

  const saveGroup = async () => {
    if (savingGroup) return;
    if (!(groupForm.name || '').trim()) { alert('Вкажіть назву'); return; }
    setSavingGroup(true);
    try {
      await lmsService.groupCreate({
        name: groupForm.name.trim(),
        parent: groupForm.parent || null,
        position: Number(groupForm.position) || 0,
      });
      setGroupOpen(false);
      setGroupForm({ name: '', parent: '', position: 0 });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally {
      setSavingGroup(false);
    }
  };

  const nameById = Object.fromEntries(groups.map((g) => [g.id, g.name]));

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MenuBookIcon color="primary" />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Курси</Typography>
        </Box>
        {manage && tab === 'courses' ? (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateCourse}>Новий курс</Button>
        ) : null}
        {manage && tab === 'groups' ? (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setGroupOpen(true)}>Нова група</Button>
        ) : null}
      </Box>

      {manage ? (
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab value="courses" label="Курси" />
          <Tab value="groups" label="Групи" />
        </Tabs>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Оберіть курс, щоб відкрити зміст і відмітити проходження.
        </Typography>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : tab === 'groups' && manage ? (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Назва</TableCell>
                <TableCell>Батьківська</TableCell>
                <TableCell>Позиція</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography color="text.secondary">Груп ще немає.</Typography>
                  </TableCell>
                </TableRow>
              ) : groups.map((g) => (
                <TableRow key={g.id} hover>
                  <TableCell>{g.name}</TableCell>
                  <TableCell>{g.parent ? (nameById[g.parent] || g.parent) : '—'}</TableCell>
                  <TableCell>{g.position}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={async () => {
                        if (!window.confirm(`Видалити групу «${g.name}»?`)) return;
                        await lmsService.groupDelete(g.id);
                        load();
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Назва</TableCell>
                <TableCell>Група</TableCell>
                <TableCell>Статус</TableCell>
                {manage ? <TableCell>Автор</TableCell> : null}
                <TableCell align="right">Дії</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={manage ? 5 : 4}>
                    <Typography color="text.secondary">
                      {manage ? 'Курсів ще немає — створіть перший.' : 'Поки немає опублікованих курсів.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : rows.map((row) => {
                const st = statusLabel(row.my_status);
                return (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600 }}>{row.title}</Typography>
                      {row.description ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {row.description}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>{row.group_name || '—'}</TableCell>
                    <TableCell>
                      {manage ? (
                        <Chip
                          size="small"
                          label={row.published ? 'Опубліковано' : 'Чернетка'}
                          color={row.published ? 'success' : 'default'}
                        />
                      ) : (
                        <Chip size="small" label={st.label} color={st.color} />
                      )}
                    </TableCell>
                    {manage ? <TableCell>{row.created_by_name || '—'}</TableCell> : null}
                    <TableCell align="right">
                      <Button size="small" variant="outlined" sx={{ mr: 0.5 }} onClick={() => navigate(`/lms/courses/${row.id}`)}>
                        Відкрити
                      </Button>
                      {manage ? (
                        <>
                          <IconButton size="small" onClick={() => openEditCourse(row)} title="Редагувати">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => togglePublish(row)} title={row.published ? 'Зняти' : 'Опублікувати'}>
                            {row.published ? <UnpublishedIcon fontSize="small" /> : <PublishIcon fontSize="small" />}
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => deleteCourse(row)} title="Видалити">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={courseOpen}
        onClose={() => !savingCourse && setCourseOpen(false)}
        maxWidth="md"
        fullWidth
        disableEnforceFocus
        disableAutoFocus
        keepMounted={false}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            <span>{editingId ? 'Редагувати курс' : 'Новий курс'}</span>
            {editingId ? (
              <LmsVersionHistory
                entityType="course"
                entityId={editingId}
                onRestored={async () => { await openEditCourse({ id: editingId }); }}
              />
            ) : null}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Назва" value={courseForm.title} onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })} fullWidth required />
            <TextField
              label="Опис"
              value={courseForm.description}
              onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              select
              label="Група"
              value={courseForm.group}
              onChange={(e) => setCourseForm({ ...courseForm, group: e.target.value })}
              fullWidth
            >
              <MenuItem value="">— без групи —</MenuItem>
              {groups.map((g) => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
            </TextField>
            <TextField
              label="Позиція"
              type="number"
              value={courseForm.position}
              onChange={(e) => setCourseForm({ ...courseForm, position: e.target.value })}
            />
            <LmsRichEditor
              label="Контент курсу"
              value={courseForm.body}
              onChange={(body) => setCourseForm({ ...courseForm, body })}
              minHeight={260}
            />
            <FormControlLabel
              control={<Switch checked={Boolean(courseForm.published)} onChange={(e) => setCourseForm({ ...courseForm, published: e.target.checked })} />}
              label="Опубліковано"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCourseOpen(false)} disabled={savingCourse}>Скасувати</Button>
          <Button variant="contained" onClick={saveCourse} disabled={savingCourse}>
            {savingCourse ? <CircularProgress size={20} /> : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={groupOpen} onClose={() => !savingGroup && setGroupOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Нова група</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Назва" value={groupForm.name} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} fullWidth required />
            <TextField
              select
              label="Батьківська група"
              value={groupForm.parent}
              onChange={(e) => setGroupForm({ ...groupForm, parent: e.target.value })}
              fullWidth
            >
              <MenuItem value="">— верхній рівень —</MenuItem>
              {groups.map((g) => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
            </TextField>
            <TextField
              label="Позиція"
              type="number"
              value={groupForm.position}
              onChange={(e) => setGroupForm({ ...groupForm, position: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupOpen(false)} disabled={savingGroup}>Скасувати</Button>
          <Button variant="contained" onClick={saveGroup} disabled={savingGroup}>
            {savingGroup ? <CircularProgress size={20} /> : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
